import { Bus, Car, Tractor, Truck, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { LiveStatus } from '@/types/api';

/**
 * The reference shows a different line-art vehicle per body type. There is no
 * body-type field on the API, so it is inferred from the model name — good
 * enough to make a list of sixteen vehicles scannable, and it falls back to a
 * plain car rather than guessing wrong loudly.
 */
const BODY_RULES: { icon: LucideIcon; match: RegExp }[] = [
  { icon: Bus, match: /coaster|bus|minibus/i },
  { icon: Truck, match: /hilux|d-?max|silverado|ranger|navara|canter|truck|tipper|pick/i },
  { icon: Truck, match: /urvan|transit|h100|express|sprinter|van/i },
  { icon: Tractor, match: /tractor|loader|excavator/i },
];

export function vehicleIcon(make?: string, model?: string): LucideIcon {
  const haystack = `${make ?? ''} ${model ?? ''}`;
  return BODY_RULES.find((rule) => rule.match.test(haystack))?.icon ?? Car;
}

const TINT: Record<LiveStatus, string> = {
  moving: 'bg-moving-soft text-moving',
  idle: 'bg-idle-soft text-idle',
  offline: 'bg-offline-soft text-offline',
  alert: 'bg-alert-soft text-alert',
};

export function VehicleThumb({
  make,
  model,
  status,
  className,
  size = 'md',
}: {
  make?: string;
  model?: string;
  status?: LiveStatus;
  className?: string;
  size?: 'sm' | 'md';
}) {
  const Icon = vehicleIcon(make, model);

  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center rounded-xl',
        size === 'sm' ? 'size-9' : 'size-11',
        status ? TINT[status] : 'bg-muted text-muted-foreground',
        className,
      )}
    >
      <Icon className={size === 'sm' ? 'size-4' : 'size-5'} strokeWidth={1.8} />
    </span>
  );
}
